import { useCallback, useMemo, useRef } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { isTraceBoardItemEqual } from '../utils/isTraceBoardItemEqual';
export function useTraceBoardItemElements(columns) {
    const columnsRef = useRef(columns);
    columnsRef.current = columns;
    const elementsRef = useRef([]);
    const subscriptionRef = useRef([]);
    const observerRef = useRef(new ResizeObserver(entries => {
        for (const entry of entries) {
            for (const itemElement of elementsRef.current) {
                const { item, element, rowIndex } = itemElement;
                if (element === entry.target) {
                    publish(item.columnId, rowIndex);
                }
            }
        }
    }));
    /**
     * Assigns the DOM element for the item card for rendering the line connectors.
     */
    const register = useCallback((rowIndex, item, element) => {
        // make sure only one reference to a column item
        const itemElement = elementsRef.current.find(c => {
            return c.rowIndex === rowIndex && isTraceBoardItemEqual(c.item, item);
        });
        if (itemElement) {
            observerRef.current.unobserve(itemElement.element);
            itemElement.element = element;
        }
        else {
            elementsRef.current.push({ rowIndex, item, element });
        }
        observerRef.current.observe(element);
    }, []);
    const unregister = useCallback((rowIndex, item) => {
        const index = elementsRef.current.findIndex(c => {
            return c.rowIndex === rowIndex && isTraceBoardItemEqual(c.item, item);
        });
        if (index > -1) {
            const [{ element }] = elementsRef.current.splice(index, 1);
            observerRef.current.unobserve(element);
        }
    }, []);
    const subscribe = useCallback((columnId, rowIndex, callback) => {
        subscriptionRef.current.push({ rowIndex, columnId, callback });
    }, []);
    const unsubscribe = useCallback((columnId, rowIndex, callback) => {
        const index = subscriptionRef.current.findIndex(s => {
            return (s.columnId === columnId &&
                s.rowIndex === rowIndex &&
                s.callback === callback);
        });
        if (index > -1) {
            subscriptionRef.current.splice(index, 1);
        }
    }, []);
    /**
     * Notify all subscriptions in current row for affected column
     * as well as columns to its left and right.
     */
    const publish = useCallback((columnId, rowIndex) => {
        const columnIndex = columnsRef.current.findIndex(column => column.id === columnId);
        for (const subscription of subscriptionRef.current) {
            const subscriptionColumnIndex = columnsRef.current.findIndex(column => column.id === subscription.columnId);
            if (subscription.rowIndex === rowIndex &&
                columnIndex > -1 &&
                subscriptionColumnIndex > -1 &&
                Math.abs(columnIndex - subscriptionColumnIndex) <= 1) {
                subscription.callback(columnId, rowIndex);
            }
        }
    }, []);
    return useMemo(() => {
        return {
            elementsRef,
            publish,
            register,
            subscribe,
            unregister,
            unsubscribe
        };
    }, [publish, register, subscribe, unregister, unsubscribe]);
}
//# sourceMappingURL=useTraceBoardItemElements.js.map